// Hideo - the script editor's toolbar (src/components/editor/EditorToolbar.vue) and its drop-down (#tbDropMenu,
// ToolbarMenu.vue: alignment, lists, Style, Font), with the script's text styles (<style id="scriptStylesCss">).
// Ported from the legacy editor.js (layoutToolbar, tbDrop, openTbMenu, placeTbMenu, tbDropPick, updTbState,
// toggleTbMore) and textstyles.js (font and style menus, applyScriptStyles, updFontStyleBtns, the default styles).
// The commands on the text (ex, setFontSize, applyFont, applyBlock…) are the writing surface
// (src/features/editor/surface/commands.ts): they read and write the shown values in `tb` (size, colours under "A" and
// the highlighter).
import { defineStore } from 'pinia'
import { computed, nextTick, reactive, ref } from 'vue'
import { t, tFill } from '../i18n'
import { useScriptsStore } from './scripts'
import { useEditorStore } from './editor'
import { syncToast, uiAlert } from './notices'
import { TB_ROWS, tbCut, tbEntries, tbRows, type TbBox } from '../features/editor/toolbar'
import { rgbHex } from '../features/editor/colors'
import { elOf, ex, restoreSel, savedRange } from '../features/editor/surface/core'
import { applyBlock, applyFont, setListKind, tbState } from '../features/editor/surface/commands'
import { selStartEl } from '../features/editor/surface/selmenu'
import {
  FONT_GROUPS, FONT_W, STYLE_KEYS, cleanStyle, defaultStylesOf, fontCss, fontKnown, fontWeights, loadEditorFonts, originalStyles,
  stylesCssText, stylesFor, type StyleSet
} from '../features/editor/styles'
import type { ProfileData, Script, StyleKey } from '../data/types'
import { saveAppData } from '../features/storage/save'
import { edBodyEl } from '../features/editor/html'

/** The first font of a computed style, without quotes */
const fontName = (cs: CSSStyleDeclaration): string => cs.fontFamily.split(',')[0].replace(/["']/g, '').trim()

type DropKind = '' | 'align' | 'list' | 'font' | 'style'
/** Font and weight under the cursor (the Font menu marks them) */
interface CurFont { ff: string; fw: number }

/** Element of the cursor's position in the page, or null */
function caretEl(): Element | null {
  const s = getSelection(), n = s && s.rangeCount ? s.anchorNode : null, el = elOf(n)
  return el || null
}
/** Block type of an element's block, for the styles ('p' for anything else) */
const blockKeyOf = (el: Element | null): StyleKey => {
  const b = el && el.closest('h1,h2,h3,h4,blockquote,pre')
  return b && edBodyEl().contains(b) ? b.tagName.toLowerCase() as StyleKey : 'p'
}

export const useToolbarStore = defineStore('toolbar', () => {
  const scripts = useScriptsStore()
  /** What the tools show (the writing surface writes the size and the colours) */
  const tb = reactive({
    align: 'justifyLeft', list: '',
    fontLabel: 'Lexend', fontFamily: '', fontWeight: '' as string | number, styleLabel: t('Text'),
    fsize: 17 as number | string, tc: '#e85d4e', hl: '#fde047', tcBar: 'transparent', hlBar: 'transparent', zoom: '1'
  })

  // ----- Layout: two rows, the rest in the ⋮ panel -----
  /** Phone order (< 768 px at the last layout) */
  const phone = ref(false)
  /** Entries in the bar (the others are in the ⋮ panel) */
  const cut = ref(Infinity)
  const moreBtn = ref(false)
  /** The ⋮ panel is open */
  const more = ref(false)
  const entries = computed(() => tbEntries(phone.value))
  let mainEl: HTMLElement | null = null, run = 0
  /** EditorToolbar.vue gives its bar (#tbMain) */
  function setMain(el: HTMLElement | null): void { mainEl = el }
  /** Lays the tools out again (opening, mode, resizing): all in the bar; if they take more than two rows, the ⋮ button
   *  and the rest in its panel. Not while the toolbar is hidden (Markdown, editor closed): done when it comes back */
  async function layout(): Promise<void> {
    const main = mainEl
    if (!main || !main.offsetParent) return
    const me = ++run
    phone.value = innerWidth < 768; cut.value = Infinity; moreBtn.value = false
    await nextTick()
    if (me !== run) return
    const boxes = (): TbBox[] => [...main.children].map(n => ({ top: (n as HTMLElement).offsetTop, width: (n as HTMLElement).offsetWidth, sep: n.tagName === 'SPAN' }))
    if (tbRows(boxes()).length <= TB_ROWS) { toggleMore(false); return }
    moreBtn.value = true
    await nextTick()
    if (me !== run) return
    const c = tbCut(boxes())
    if (c >= 0) cut.value = c
  }
  /** Opens or closes the ⋮ panel (on = toggles) */
  function toggleMore(on?: boolean): void { more.value = on ?? !more.value }

  // ----- State of the tools: alignment, list, style and font where the cursor is -----
  /** Style and Font buttons: those of the text under the cursor */
  function updFontStyleBtns(el: Element): void {
    const cs = getComputedStyle(el), fam = fontName(cs), w = +cs.fontWeight || 400
    const known = fontKnown(fam)
    tb.fontLabel = known ? (w !== 400 && w !== 700 && fontWeights(fam).includes(w) ? tFill(FONT_W[w], { font: fam }) : fam) : t('Font')
    tb.fontFamily = known ? fontCss(fam) : ''; tb.fontWeight = known && w !== 700 ? w : ''
    tb.styleLabel = (STYLE_KEYS.find(x => x[0] === blockKeyOf(el)) as [StyleKey, string])[1]
  }
  /** The drop-down buttons follow the cursor (alignment, list), and Style and Font show those of its text */
  function updState(): void {
    const st = tbState()
    tb.align = st.align; tb.list = st.list
    const el = caretEl()
    if (el && edBodyEl().contains(el)) updFontStyleBtns(el)
  }

  // ----- The drop-down under a button: alignment, lists, Style, Font -----
  const drop = reactive({ kind: '' as DropKind, btn: '', cur: '' as string, font: { ff: '', fw: 400 } as CurFont, seq: 0 })
  /** Fonts whose variants are shown; the style whose actions are shown */
  const fontOpen = reactive(new Set<string>())
  const styleOpen = ref('')
  let menuEl: HTMLElement | null = null
  /** ToolbarMenu.vue gives its element (#tbDropMenu) */
  function setMenu(el: HTMLElement | null): void { menuEl = el }
  /** Under the button (or above it when there is more room), never out of the screen */
  function place(): void {
    const m = menuEl, btn = drop.btn ? document.getElementById(drop.btn) : null
    if (!m || !btn || !drop.kind) return
    const r = btn.getBoundingClientRect()
    m.style.maxHeight = ''
    const h = m.offsetHeight, below = innerHeight - r.bottom - 12, above = r.top - 12
    m.style.left = Math.max(8, Math.min(r.left, innerWidth - m.offsetWidth - 8)) + 'px'
    if (h <= below || below >= above) { m.style.top = (r.bottom + 4) + 'px'; m.style.maxHeight = below + 'px' }
    else { m.style.maxHeight = above + 'px'; m.style.top = Math.max(8, r.top - Math.min(h, above) - 4) + 'px' }
  }
  /** Opens the drop-down under its button (btn = the button's id; wide: Style and Font) */
  async function open(kind: DropKind, btn: string, wide: boolean): Promise<void> {
    drop.kind = kind; drop.btn = btn; drop.seq++
    await nextTick()
    if (menuEl) menuEl.style.width = wide ? 'min(19rem, calc(100vw - 1rem))' : ''
    place()
  }
  function hideDrop(): void { drop.kind = ''; drop.btn = '' }
  /** Alignment or lists (the button again closes it) */
  function tbDrop(kind: 'align' | 'list', btn: string): void {
    if (drop.kind === kind) return hideDrop()
    drop.cur = tbState()[kind]
    void open(kind, btn, false)
  }
  function dropPick(kind: 'align' | 'list', v: string): void {
    hideDrop()
    if (kind === 'align') ex(v)
    else if (v === 'indent' || v === 'outdent') ex(v)
    else { edBodyEl().focus(); restoreSel(); setListKind(v) }
    updState()
  }
  /** Font and weight of the text under the cursor */
  function curFont(): CurFont {
    const el = caretEl()
    if (!el || !edBodyEl().contains(el)) return { ff: '', fw: 400 }
    const cs = getComputedStyle(el)
    return { ff: fontName(cs), fw: +cs.fontWeight || 400 }
  }
  /** Font menu: the current font marked and in view, the variants of its family shown */
  async function openFont(btn: string): Promise<void> {
    if (drop.kind === 'font') return hideDrop()
    loadEditorFonts()
    const cur = curFont()
    fontOpen.clear(); FONT_GROUPS.forEach(([, l]) => l.forEach(f => { if ((f[2] || []).includes(cur.ff)) fontOpen.add(f[0]) }))
    drop.font = cur
    await open('font', btn, true)
    const on = menuEl && menuEl.querySelector('.text-brand')
    if (on) on.scrollIntoView({ block: 'center' })
  }
  /** Shows or hides a font's variants (the list keeps its scroll position) */
  async function toggleFontOpen(key: string): Promise<void> {
    const m = menuEl, top = m ? m.scrollTop : 0
    if (fontOpen.has(key)) fontOpen.delete(key); else fontOpen.add(key)
    drop.font = curFont(); drop.seq++
    await nextTick()
    place(); if (m) m.scrollTop = top
  }
  /** Applies a font (applyFont of the writing surface: on the selection, or the word under the cursor) */
  const pickFont = (n: string, w: number) => applyFont(n, w || 0)

  // ----- Styles of the script (Style menu) -----
  /** Block type under the cursor */
  const curBlockKey = (): StyleKey => blockKeyOf(caretEl())
  /** The styles in use for the open script */
  function scriptStyles(): StyleSet { const [d, sc] = scripts.current(); return stylesFor(d, sc) }
  /** <style id="scriptStylesCss">: the open script's styles (none with the editor closed) */
  function applyStyles(): void {
    let st = document.getElementById('scriptStylesCss')
    if (!st) { st = document.createElement('style'); st.id = 'scriptStylesCss'; document.head.appendChild(st) }
    st.textContent = stylesCssText(scripts.editingId ? scriptStyles() : {})
  }
  async function openStyle(btn: string): Promise<void> {
    if (drop.kind === 'style') return hideDrop()
    styleOpen.value = ''; drop.cur = curBlockKey()
    await open('style', btn, true)
  }
  /** The Style menu again (after a change), keeping its scroll position */
  async function redrawStyle(): Promise<void> {
    const m = menuEl, top = m ? m.scrollTop : 0
    drop.cur = curBlockKey(); drop.seq++
    await nextTick()
    place(); if (m) m.scrollTop = top
  }
  function toggleStyleOpen(k: string): void { styleOpen.value = styleOpen.value === k ? '' : k; void redrawStyle() }
  /** A style picked: its block type on the paragraph */
  function pickStyle(k: string): void { hideDrop(); applyBlock(k); setTimeout(updState) }
  /** Changes the script's or the profile's styles, saves and draws them */
  function setStyles(fn: (d: ProfileData, sc: Script) => void): void {
    useEditorStore().save()
    const [d, sc] = scripts.current()
    if (!sc) return
    fn(d, sc); saveAppData(d, true); applyStyles()
    if (drop.kind === 'style') void redrawStyle()
  }
  /** "Update to match the selected text": font, weight, size, italic, underline and colour of the text become the style */
  function updateStyleFromSel(k: StyleKey): unknown {
    const s = getSelection() as Selection, r = s.rangeCount && edBodyEl().contains(s.getRangeAt(0).commonAncestorContainer) ? s.getRangeAt(0) : savedRange
    const el = r && selStartEl(r)
    if (!el) return uiAlert(t('Put the cursor in (or select) the text to use as a model.'))
    const cs = getComputedStyle(el), blk = el.closest('h1,h2,h3,h4,blockquote,pre,p,li,div') || el
    let colEl: Element | null = null
    for (let e: Element | null = el; e && e !== blk.parentElement; e = e.parentElement) if ((e as HTMLElement).style && (e as HTMLElement).style.color || e.getAttribute && e.getAttribute('color')) { colEl = e; break }
    const v = cleanStyle({
      ff: fontName(cs), fw: Math.round((+cs.fontWeight || 400) / 100) * 100, fs: parseFloat(cs.fontSize),
      it: cs.fontStyle === 'italic', ul: !!el.closest('u') || /underline/.test(cs.textDecorationLine), c: colEl ? rgbHex(getComputedStyle(colEl).color) : ''
    })
    setStyles((_d, sc) => { sc.styles = sc.styles || {}; (sc.styles as Record<string, unknown>)[k] = v })
    styleOpen.value = ''; void redrawStyle()
  }
  function resetStyle(k: StyleKey): void { setStyles((_d, sc) => { if (sc.styles) delete sc.styles[k] }); styleOpen.value = ''; void redrawStyle() }
  function resetAllStyles(): void { setStyles((_d, sc) => { sc.styles = originalStyles() }) }
  function useDefaultStyles(): void { setStyles((_d, sc) => { delete sc.styles }) }
  function saveDefaultStyles(): void {
    const R = scriptStyles()
    setStyles(d => { d.settings.scriptStyles = defaultStylesOf(R) })
    syncToast(t('Default styles saved: they apply to scripts without their own styles'))
  }
  function clearDefaultStyles(): void { setStyles(d => { delete d.settings.scriptStyles }) }

  return {
    tb, phone, cut, moreBtn, more, entries, setMain, layout, toggleMore, updState,
    drop, fontOpen, styleOpen, setMenu, place, open, hideDrop, tbDrop, dropPick, openFont, toggleFontOpen, pickFont,
    scriptStyles, applyStyles, openStyle, toggleStyleOpen, pickStyle, updateStyleFromSel,
    resetStyle, resetAllStyles, useDefaultStyles, saveDefaultStyles, clearDefaultStyles
  }
})
